// @flow

import { useState, useEffect, useMemo, useCallback } from 'react'
import Cookie from 'js-cookie'

const IOS_STORE_URL = 'https://apps.apple.com/jp/app/id973447544'
const ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.spacemarket'

/**
 * @description
 * アプリリンク用のカスタムフック
 * 全ブラウザでApp Link（Universal Links / App Links）を使用
 * アプリがインストールされていない場合はストアへフォールバック
 *
 * 使い方:
 * const { appLinkHref, handleAppLinkClick } = useAppLink({ appLinkUrl })
 * <a href={appLinkHref} onClick={handleAppLinkClick}>開く</a>
 */
export const useAppLink = ({ appLinkUrl, afDp } = {}) => {
  /**
   * プロモーションリンクのCookie連携
   * cookieに入っているホストのIDと有効期限をURLパラメータとして付与
   * 「&pl_<hostId>=<timestamp>&pl_<hostId>=<timestamp>」の形式
   */
  const promotionLinkParams = useMemo(() => {
    const cookies = Cookie.get() || {}
    const promotionLinkCookies = Object.entries(cookies).filter(([cookieKey]) =>
      cookieKey.includes('sm_pl'),
    )

    if (promotionLinkCookies.length === 0) return ''

    const formattedPromotionLinkCookies = promotionLinkCookies.map(
      ([cookieKey, timestamp]) => {
        const capturingOwnerIdRegex = /^sm_pl_(?<ownerId>\d+)$/
        const found = capturingOwnerIdRegex.exec(cookieKey)
        const ownerId = found?.groups?.ownerId
        if (!ownerId) return ''
        return `&pl_${ownerId}=${String(timestamp)}`
      },
    )

    return formattedPromotionLinkCookies.join('')
  }, [])

  /**
   * アプリリンク用のURL
   * Universal Links / App Links で使用するhref属性値
   * af_dpを最後に配置（アプリ側でのパース都合）
   */
  const [appLinkHref, setAppLinkHref] = useState(appLinkUrl || '')

  useEffect(() => {
    if (!appLinkUrl) return

    const deepLink = afDp ?? window.location.href
    // promotionLinkParamsが空でない場合は先頭の&を?に置き換え、空なら?から始める
    const queryStart = promotionLinkParams
      ? `?${promotionLinkParams.slice(1)}&af_dp=${deepLink}`
      : `?af_dp=${deepLink}`
    setAppLinkHref(`${appLinkUrl}${queryStart}`)
  }, [afDp, appLinkUrl, promotionLinkParams])

  /**
   * クリック時のフォールバック処理
   * - PC: 即座にストアへリダイレクト（デフォルト遷移を防止）
   * - iOS/Android: hrefでそのまま遷移
   *   - アプリあり: Universal Links / App Links でアプリ起動
   *   - アプリなし: S3のindex.htmlがUA判定してストアへリダイレクト
   */
  const handleAppLinkClick = useCallback(e => {
    if (typeof window === 'undefined') return

    const ua = window.navigator.userAgent.toLowerCase()
    const isAndroid = ua.includes('android')
    const isIOS =
      ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')
    const isMobile = isAndroid || isIOS
    const storeUrl = isAndroid ? ANDROID_STORE_URL : IOS_STORE_URL

    // PCの場合は即座にストアへリダイレクト
    if (!isMobile) {
      e.preventDefault()
      window.location.href = storeUrl
    }
  }, [])

  return { appLinkHref, handleAppLinkClick }
}
